/**
 * Public setup-invitation API: the invitee creates their program.
 *
 * **Anonymous**, so it bypasses the shared api client and its auth header, exactly as
 * `services/invitations.ts` does: the caller has no account yet.
 *
 * **The token travels in the POST body, never the URL.** The request path is logged to a long-lived
 * file sink and sent to Sentry, while the token is the whole credential. See the longer note on
 * `PREVIEW_URL` in `services/invitations.ts`.
 */

import type {
  AcceptSetupInvitationBody,
  AcceptSetupInvitationRequest,
  AcceptedSetupInvitation,
  SetupInvitationFailureKind,
  SetupInvitationPreview,
  SetupInvitationPreviewBody,
} from '../types/setupInvitations';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5206';

const PREVIEW_URL = `${API_BASE_URL}/api/public/setup-invitations/preview`;
const ACCEPT_URL = `${API_BASE_URL}/api/public/setup-invitations/accept`;

const DEFAULT_MESSAGES: Record<SetupInvitationFailureKind, string> = {
  invalid: 'This invitation is invalid or has expired.',
  invalid_input: 'Check the details you entered and try again.',
  name_taken: 'A program with that name already exists. Try a different name.',
  already_registered: 'An account already exists for this email address.',
  rate_limited: 'Too many attempts. Wait a minute and try again.',
  unavailable: 'Something went wrong on our end. Your invitation link still works, so please try again.',
};

/** A refused public setup call, carrying the state the page should render. */
export class SetupInvitationError extends Error {
  readonly kind: SetupInvitationFailureKind;
  /** The body's `code`, when it carried one (`invalid_password`, `org_name_taken`, ...). */
  readonly code: string | null;

  constructor(kind: SetupInvitationFailureKind, message: string, code: string | null = null) {
    super(message);
    this.name = 'SetupInvitationError';
    this.kind = kind;
    this.code = code;
  }
}

/**
 * Status code (and 409 code) to rendered state.
 *
 * Anything unmapped falls to `unavailable`, not `invalid`: telling someone their link is dead when
 * the server merely hiccuped costs them the invitation. An unknown 409 keeps the form up with the
 * server's message rather than guessing which of the two known conflicts it was.
 */
const failureKindFor = (status: number, code: string | null): SetupInvitationFailureKind => {
  switch (status) {
    case 400:
      return 'invalid_input';
    case 404:
      return 'invalid';
    case 409:
      if (code === 'org_name_taken') return 'name_taken';
      if (code === 'email_registered') return 'already_registered';
      return 'invalid_input';
    case 429:
      return 'rate_limited';
    default:
      return 'unavailable';
  }
};

const readFailure = async (response: Response): Promise<SetupInvitationError> => {
  const body = (await response.json().catch(() => null)) as
    | { message?: unknown; code?: unknown }
    | null;
  const code = typeof body?.code === 'string' && body.code ? body.code : null;
  const kind = failureKindFor(response.status, code);
  const message = body?.message;

  return new SetupInvitationError(
    kind,
    typeof message === 'string' && message.trim() ? message : DEFAULT_MESSAGES[kind],
    code
  );
};

/** Typed as the union of the two wire bodies, so an extra field fails to compile. */
const postToken = (url: string, body: SetupInvitationPreviewBody | AcceptSetupInvitationBody) =>
  fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

const unavailable = () =>
  new SetupInvitationError('unavailable', DEFAULT_MESSAGES.unavailable);

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

/** What the page is told before anyone types a password. */
export const getSetupInvitationPreview = async (
  token: string
): Promise<SetupInvitationPreview> => {
  let response: Response;

  try {
    response = await postToken(PREVIEW_URL, { token });
  } catch {
    throw unavailable();
  }

  if (!response.ok) throw await readFailure(response);

  const row = asRecord(await response.json().catch(() => null));
  const email = text(row.email);
  // Without the address there is nothing to create an account for. Route a skewed body to retry.
  if (!email) throw unavailable();

  return {
    email,
    programName: text(row.programName),
    sport: text(row.sport),
    requiresPayment: row.requiresPayment === true,
    annualPriceCents:
      typeof row.annualPriceCents === 'number' && Number.isFinite(row.annualPriceCents)
        ? row.annualPriceCents
        : null,
    schoolName: text(row.schoolName),
  };
};

/**
 * Creates the account and the program. The body is exactly
 * `{ token, firstName, lastName, password, organizationName, sport }`, built field by field.
 *
 * **No session is issued.** The invitee signs in normally afterwards.
 */
export const acceptSetupInvitation = async (
  token: string,
  request: AcceptSetupInvitationRequest
): Promise<AcceptedSetupInvitation> => {
  let response: Response;

  try {
    response = await postToken(ACCEPT_URL, {
      token,
      firstName: request.firstName,
      lastName: request.lastName,
      password: request.password,
      organizationName: request.organizationName,
      sport: request.sport,
    });
  } catch {
    throw unavailable();
  }

  if (!response.ok) throw await readFailure(response);

  const row = asRecord(await response.json().catch(() => null));
  const email = text(row.email);
  const organizationName = text(row.organizationName);
  if (!email || !organizationName) throw unavailable();

  return { email, organizationName, requiresPayment: row.requiresPayment === true };
};
